/**
 * Shared boundary calculations for VBL analysis
 * Ensures consistency between data generation and chart rendering
 */

export interface VblBoundaries {
  dataMin: number;
  dataMax: number;
  curveMin: number;  // Extended minimum for theoretical curves
  curveMax: number;  // Extended maximum for theoretical curves
  logMinBoundary: number;  // Log scale minimum boundary
  logMaxBoundary: number;  // Log scale maximum boundary
}

/**
 * Calculate consistent boundaries for VBL analysis
 * Used by both the analysis hook and chart components
 */
export function calculateVblBoundaries(degradationTimes: number[]): VblBoundaries {
  if (degradationTimes.length === 0) {
    return {
      dataMin: 1,
      dataMax: 1000,
      curveMin: 0.5,
      curveMax: 2000,
      logMinBoundary: 0.1,
      logMaxBoundary: 2000,
    };
  }

  const dataMin = Math.min(...degradationTimes);
  const dataMax = Math.max(...degradationTimes);

  // Extended range for theoretical curves and confidence intervals
  // Use more conservative extension to avoid extreme ranges
  const curveMin = Math.max(0.1, dataMin * 0.5); // Start at 50% of minimum or 0.1 days minimum
  const curveMax = dataMax * 1.5; // Extend to 150% of maximum for better focus on data

  // Log scale boundaries using nice values for days
  // Use data-focused boundaries instead of extreme curve boundaries
  const getLogBoundaries = (dataMin: number, dataMax: number): { min: number; max: number } => {
    const niceValues = [0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000];

    // For min: go one step below dataMin, but not below 0.1
    let logMin = 0.1;
    for (let i = niceValues.length - 1; i >= 0; i--) {
      if (niceValues[i] < dataMin) {
        logMin = niceValues[i];
        break;
      }
    }

    // For max: go one step above dataMax
    let logMax = 10000;
    for (let i = 0; i < niceValues.length; i++) {
      if (niceValues[i] > dataMax) {
        logMax = niceValues[i];
        break;
      }
    }

    // Ensure reasonable boundaries for common VBL ranges
    if (dataMin >= 1 && dataMax <= 2000) {
      logMin = Math.max(0.5, logMin); // Don't go below 0.5 for typical data
      logMax = Math.min(5000, logMax); // Don't exceed 5000 for typical data
    }

    return { min: logMin, max: logMax };
  };

  const logBoundaries = getLogBoundaries(dataMin, dataMax);
  const logMinBoundary = logBoundaries.min;
  const logMaxBoundary = logBoundaries.max;

  return {
    dataMin,
    dataMax,
    curveMin,
    curveMax,
    logMinBoundary,
    logMaxBoundary,
  };
}